import React from 'react';
import Axios from 'axios';
import { observer } from 'mobx-react';

import { SERVER_URL } from 'Constants';
import useAsync from 'Hooks/useAsync';
import { useOptionalMessage } from 'Hooks/useOptionalMessage';
import { CargoEntity } from 'Models/Entities';
import { getLowerCaseMessageByKey, getPascalCaseMessageByKey } from 'Stores/LanguageStore';
import alertToast from 'Util/ToastifyUtils';
import { Button, Colors, Display, Sizes } from 'Views/Components/Button/Button';
import { LottieSpinner } from 'Views/Components/_HumanWritten/Lottie/LottieSpinner';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { confirmModal } from 'Views/Components/Modal/ModalUtils';
import { newConfirmModal } from 'Views/Components/Modal/NewConfirmModal';
import { TextInput } from 'Views/Components/New/TextInput';
import { fetchPreviousVehicles } from '../../../../Util/_HumanWritten/VehicleMakeAndModelUtils';
import If from '../../If/If';

const DEFAULT_VISIBLE_COUNT = 3;

export interface LatestCargosProps {
	userId: string;
	refetchVehicleTabData?: () => void;
	onSelect?: (cargoInfo?: CargoEntity) => void;
	update?: (vehicles: CargoEntity[]) => void;
	previousCargos?: CargoEntity[];
	forCheckIn?: boolean;
}

function LatestCargosList({
	userId,
	refetchVehicleTabData,
	onSelect,
	update,
	previousCargos,
	forCheckIn = false,
}: LatestCargosProps) {
	const fmtOptional = useOptionalMessage();
	const [vehicles, setVehicles] = React.useState<CargoEntity[]>(previousCargos ?? []);
	const [searchQuery, setSearchQuery] = React.useState('');
	const [showAll, setShowAll] = React.useState(false);
	const response = useAsync(() => {
		if (previousCargos !== undefined) {
			return new Promise<CargoEntity[]>(resolve => resolve([]));
		}
		return fetchPreviousVehicles(userId);
	}, [userId]);
	const setParentVehicles = (x: CargoEntity[]) => {
		if (update) {
			update(x);
		}
	};

	React.useEffect(() => {
		if (!response.data || previousCargos !== undefined) {
			if (previousCargos !== undefined) {
				setParentVehicles(previousCargos);
			}

			return;
		}
		const enabledCargos = response.data.filter(x => !x.disabled);
		setVehicles(enabledCargos);
		setParentVehicles(enabledCargos);
	}, [response.data]);

	const filteredVehicles = React.useMemo(() => {
		if (searchQuery.trim()) {
			const query = searchQuery.toLowerCase().trim();
			return vehicles.filter(v => {
				const rego = v.cargoIdentification?.toLowerCase() ?? '';
				const make = v.cargoType?.cargoMake?.toLowerCase() ?? '';
				const model = v.cargoType?.cargoModel?.toLowerCase() ?? '';
				return rego.includes(query) || make.includes(query) || model.includes(query);
			});
		}

		if (showAll) {
			return vehicles;
		}

		return vehicles.slice(0, DEFAULT_VISIBLE_COUNT);
	}, [vehicles, searchQuery, showAll]);

	if (response.type === 'loading') {
		return <LottieSpinner />;
	}

	if (response.type === 'error') {
		return null;
	}

	if (vehicles.length === 0) {
		return null;
	}

	const vehicleNoun = getLowerCaseMessageByKey('vehicle__name__label', 'vehicle');
	const regoLabel = getPascalCaseMessageByKey('vehicle-tab__rego__label', 'Rego');

	const onRemove = async (vehicle: CargoEntity) => {
		if (!vehicle.id) {
			return;
		}

		const makeModel = vehicle.cargoType?.getFormattedVehicleMakeModel();
		const rego = vehicle.cargoIdentification;
		const vehicleDescriptor = makeModel
			? `${makeModel}${rego ? ` (${rego})` : ''}`
			: (rego ?? `this ${vehicleNoun}`);

		const confirmed = await newConfirmModal(
			`Remove ${vehicleNoun}?`,
			<>
				Are you sure you want to remove <strong>{vehicleDescriptor}</strong> from your saved {vehicleNoun}s?
				This can&apos;t be undone.
			</>,
			{
				confirmText: 'Remove',
				cancelText: 'Cancel',
				resolveOnCancel: true,
			},
		).catch(() => false);

		if (!confirmed) {
			return;
		}

		const result = await Axios.post(`${SERVER_URL}/api/entity/CargoEntity/disable/${vehicle.id}`);
		if (result.status !== 200) {
			alertToast('There was an error removing this vehicle.', 'error', 'Failed to delete vehicle');
			return;
		}

		setVehicles(currentVehicles => {
			const newVehicles = currentVehicles.filter(x => x.id !== vehicle.id);
			setParentVehicles(newVehicles);
			return newVehicles;
		});

		if (refetchVehicleTabData) {
			refetchVehicleTabData();
		}

		alertToast('The vehicle has been removed from your account.', 'success', 'Vehicle deleted');
	};

	const hasMoreVehicles = !searchQuery.trim() && !showAll && vehicles.length > DEFAULT_VISIBLE_COUNT;

	const description = fmtOptional({
		id: 'previous-vehicles__description',
		defaultMessage: `Re-use one of your saved ${vehicleNoun}s, or add a new one below.`,
	});

	return (
		<section className="previous-vehicle-section booking-section-card">
			<header className="booking-section-header">
				<If condition={!forCheckIn}>
					<span className="booking-section-heading__icon">
						<Icon name="directions_car" useMaterialIcons />
					</span>
				</If>
				<div className="booking-section-header__text">
					<h4 className="booking-section-title">Pick a previous {vehicleNoun}</h4>
					<If condition={!forCheckIn}>
						{description && <p className="booking-section-description">{description}</p>}
					</If>
				</div>
			</header>
			{vehicles.length > DEFAULT_VISIBLE_COUNT && (
				<div className="previous-vehicle-search">
					<TextInput
						value={searchQuery}
						onChange={event => setSearchQuery(event.target.value)}
						placeholder={`Search by ${getLowerCaseMessageByKey('vehicle-tab__rego__label', 'rego')}, make or model`}
						startAdornment={<Icon name="search" useMaterialIcons classname="text-base leading-none" />}
					/>
				</div>
			)}
			<div className="previous-vehicle-list">
				{filteredVehicles.map(x => (
					<div className="previous-vehicle-option" key={x.id}>
						<div className="previous-vehicle-info-section">
							<If condition={x.cargoType !== null}>
								<p className="previous-vehicle-type">{x.cargoType?.getFormattedVehicleMakeModel()}</p>
							</If>
							<p className="previous-vehicle-rego">
								{regoLabel}: {x.cargoIdentification}
							</p>
						</div>
						<div className="previous-vehicle-actions">
							<Button
								className="previous-vehicle-select-button"
								display={Display.Outline}
								sizes={Sizes.Small}
								colors={Colors.Grey}
								newStyles
								onClick={() => {
									if (onSelect) {
										onSelect(x);
									}
								}}
							>
								Select
							</Button>
							<button
								type="button"
								className="previous-vehicle-remove-button"
								aria-label="Remove vehicle"
								onClick={() => onRemove(x)}
							>
								<Icon name="delete" useMaterialIcons />
							</button>
						</div>
					</div>
				))}
			</div>
			<If condition={hasMoreVehicles}>
				<Button
					className="previous-vehicle-see-more"
					display={Display.Text}
					colors={Colors.Secondary}
					newStyles
					onClick={() => setShowAll(true)}
				>
					See more ({vehicles.length - DEFAULT_VISIBLE_COUNT})
				</Button>
			</If>
			<If condition={searchQuery.trim() !== '' && filteredVehicles.length === 0}>
				<p className="previous-vehicle-no-results">
					No {vehicleNoun}s found matching &quot;{searchQuery}&quot;
				</p>
			</If>
		</section>
	);
}

export default observer(LatestCargosList);
